import { useMemo } from 'react';
import {
    BudgetCategoryGroupFieldsFragment,
    BudgetPurpose,
    TransactionEntryCategoryGroupFieldsFragment,
} from '../../graphql/generated';
import { DateRange } from '../../types';
import { sortBy } from '../../util/arrays';
import { roundCentsToNearestDollar } from '../../util/currency';
import { getTotalMonths } from '../../util/dateTime';
import { isNotNullish } from '../../util/types';
import { CashflowTransactionEntry } from '../cashflow';
import { isSpendingBudgetPurpose } from './budgetPurpose';
import { buildBudgetCategoryGroupMap } from './buildBudgetCategoryGroupMap';
import { filterTransactionEntryCategoryGroupsForBudgetPurpose } from './filterTransactionEntryCategoryGroupsForBudgetPurpose';
import { sumCategories } from './sumCategories';

export interface CategoryGroupWithSpending
    extends TransactionEntryCategoryGroupFieldsFragment {
    spendingAmount: number;
    enabled: boolean;
    isOver: boolean;
    budgetedAmount: number;
    budgetPurpose: BudgetPurpose;
}

export const useCategoryGroupsWithSpendingForBudgetPurpose = ({
    budgetCategoryGroups,
    transactionEntries,
    transactionEntryCategoryGroups,
    budgetPurpose,
    dateRange,
}: {
    budgetCategoryGroups: BudgetCategoryGroupFieldsFragment[];
    transactionEntries: CashflowTransactionEntry[];
    transactionEntryCategoryGroups: TransactionEntryCategoryGroupFieldsFragment[];
    budgetPurpose: BudgetPurpose;
    dateRange: DateRange;
}): CategoryGroupWithSpending[] =>
    useMemo(() => {
        const filteredTransactionEntryCategoryGroups =
            filterTransactionEntryCategoryGroupsForBudgetPurpose({
                budgetCategoryGroups,
                transactionEntryCategoryGroups,
                budgetPurpose,
            });

        const { categoryGroupSums } = sumCategories({
            transactionEntries,
        });

        return filteredTransactionEntryCategoryGroups.map((group) =>
            buildCategoryGroup({
                group,
                budgetCategoryGroups,
                categoryGroupSums,
                budgetPurpose,
                dateRange,
            })
        );
    }, [
        budgetCategoryGroups,
        transactionEntries,
        transactionEntryCategoryGroups,
        budgetPurpose,
        dateRange,
    ]);

export const useAllCategoryGroupsWithSpending = ({
    budgetCategoryGroups,
    transactionEntries,
    transactionEntryCategoryGroups,
    dateRange,
}: {
    budgetCategoryGroups: BudgetCategoryGroupFieldsFragment[];
    transactionEntries: CashflowTransactionEntry[];
    transactionEntryCategoryGroups: TransactionEntryCategoryGroupFieldsFragment[];
    dateRange: DateRange;
}): CategoryGroupWithSpending[] =>
    useMemo(() => {
        const budgetCategoryGroupMap =
            buildBudgetCategoryGroupMap(budgetCategoryGroups);

        const { categoryGroupSums } = sumCategories({
            transactionEntries,
        });

        const categoryGroupsWithSpending = transactionEntryCategoryGroups
            .map((group) => {
                const budgetCategoryGroup = budgetCategoryGroupMap[group.id];
                const budgetPurpose =
                    budgetCategoryGroup?.budgetPurpose ||
                    group.defaultBudgetPurpose;

                if (!isSpendingBudgetPurpose(budgetPurpose)) return null;

                return buildCategoryGroup({
                    group,
                    budgetCategoryGroups,
                    categoryGroupSums,
                    budgetPurpose,
                    dateRange,
                });
            })
            .filter(isNotNullish);

        return sortBy(categoryGroupsWithSpending, (g) => g.name);
    }, [
        budgetCategoryGroups,
        transactionEntries,
        transactionEntryCategoryGroups,
        dateRange,
    ]);

const buildCategoryGroup = ({
    group,
    budgetCategoryGroups,
    categoryGroupSums,
    budgetPurpose,
    dateRange,
}: {
    group: TransactionEntryCategoryGroupFieldsFragment;
    budgetCategoryGroups: BudgetCategoryGroupFieldsFragment[];
    categoryGroupSums: Array<{ id: string; amount: number }>;
    budgetPurpose: BudgetPurpose;
    dateRange: DateRange;
}): CategoryGroupWithSpending => {
    const budgetCategoryGroup = budgetCategoryGroups.find(
        (b) => b.categoryGroup?.id === group.id
    );

    const categoryGroupSum = categoryGroupSums.find(
        (categoryGroupSum) => categoryGroupSum.id === group.id
    );
    const rawAmount = categoryGroupSum?.amount || 0;
    const spendingAmount = roundCentsToNearestDollar(
        rawAmount * (isSpendingBudgetPurpose(budgetPurpose) ? -1 : 1)
    );
    const totalMonths = getTotalMonths(dateRange);
    const budgetedAmount = (budgetCategoryGroup?.amount || 0) * totalMonths;

    return {
        ...group,
        spendingAmount,
        enabled: budgetCategoryGroup?.enabled || false,
        isOver: budgetCategoryGroup ? spendingAmount > budgetedAmount : false,
        budgetPurpose,
        budgetedAmount: budgetedAmount,
    };
};
